import React from "react";
import { Sparkles, Hammer, ShieldCheck, Handshake, Crown } from "lucide-react";

/**
 * BreederTierBadge — v3.99c.53
 *
 * Tier classification badge used across the platform: storefronts, directory
 * cards, admin queue, and vendor dashboard. Keeps the visual language for the
 * five-tier system in one place.
 *
 * Tiers (lowest → highest):
 *   collector_grower → forging_breeder → forged_breeder → forge_partner → forge_master
 *
 * Props:
 *   - tier (string): one of the 5 tier ids
 *   - size ("sm" | "md" | "lg"): visual scale (default "md")
 *   - showLabel (bool): show the text label next to icon (default true)
 *   - subtitle (bool): show the one-line subtitle under the badge (default false)
 *   - className (string): additional Tailwind classes
 */

export const TIER_META = {
  collector_grower: {
    label: "Collector / Grower",
    subtitle: "Collector and grower account.",
    icon: Sparkles,
    color: "var(--text-muted)",
    bg: "transparent",
    border: "var(--border)",
  },
  forging_breeder: {
    label: "Forging Breeder",
    subtitle: "Building catalog and genetics for future CannaForge review.",
    icon: Hammer,
    color: "#D4A056",  // warm forge-amber
    bg: "rgba(212,160,86,0.08)",
    border: "rgba(212,160,86,0.45)",
  },
  forged_breeder: {
    label: "Forged Breeder",
    subtitle: "Approved breeder profile on CannaForge.",
    icon: ShieldCheck,
    color: "var(--gold)",
    bg: "rgba(174,138,28,0.10)",
    border: "var(--gold)",
  },
  forge_partner: {
    label: "Forge Partner",
    subtitle: "Verified partner brand on CannaForge.",
    icon: Handshake,
    color: "var(--primary-text)",
    bg: "rgba(127,184,73,0.10)",
    border: "var(--primary-text)",
  },
  forge_master: {
    label: "Forge Master",
    subtitle: "Top-tier trusted breeder recognized by CannaForge.",
    icon: Crown,
    color: "#E8C46B",
    bg: "rgba(232,196,107,0.10)",
    border: "#E8C46B",
  },
};

const SIZE_STYLES = {
  sm: { pad: "px-2 py-0.5", text: "text-[10px]", icon: "w-3 h-3" },
  md: { pad: "px-2.5 py-1", text: "text-[11px]", icon: "w-3.5 h-3.5" },
  lg: { pad: "px-3 py-1.5", text: "text-xs", icon: "w-4 h-4" },
};

export default function BreederTierBadge({
  tier,
  size = "md",
  showLabel = true,
  subtitle = false,
  className = "",
}) {
  const meta = TIER_META[tier] || TIER_META.forged_breeder;
  const Icon = meta.icon;
  const s = SIZE_STYLES[size] || SIZE_STYLES.md;
  return (
    <div className={`inline-flex flex-col items-start ${className}`} data-testid={`tier-badge-${tier}`}>
      <span
        className={`inline-flex items-center gap-1.5 ${s.pad} ${s.text} font-mono uppercase tracking-[0.18em] border`}
        style={{ color: meta.color, background: meta.bg, borderColor: meta.border }}
      >
        <Icon className={s.icon} />
        {showLabel && meta.label}
      </span>
      {subtitle && meta.subtitle && (
        <span className="text-[11px] mt-1 leading-snug" style={{ color: "var(--text-muted)" }}>
          {meta.subtitle}
        </span>
      )}
    </div>
  );
}
